/**
 * Horizontal image strip of random order-menu items (same catalog API as Order tab).
 * Wrapped in `ContentCard` so left/right inset matches `DashboardScrollableGridCard` / macros cards.
 * Used by: DashboardScreen — `OnlineOrderImagesStrip`.
 */
import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, useWindowDimensions } from 'react-native';
import type { ThemeColors } from '../../shared/theme';
import { appTheme } from '../../shared/theme';
import { getApiName, getLocale, useTranslation } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { DASH_STUDIO_INNER, studioCardShell } from '../../shared/theme/studio/dashboardConstants';
import { resolveImageUri } from '../../shared/utils/resolveImageUri';
import { HorizontalLtrScrollView } from '../../components/ui/HorizontalLtrScrollView';
import { ContentCard } from '../../components/common/ContentCard';
import { useOrderMenuQuery, type OrderMenuCartItem } from '../../data/domains/orders';
import { MenuImageTile, menuImagesStripStyles, computeMenuImageTileMetrics, type FlatItem } from './DashboardMenuImagesStrip';
import { ButtonLoader } from '../../components/load-main/ButtonLoader';

/** Inclusive 7–10; stable for this mount so the strip does not flicker on re-renders. */
function random_strip_count(): number {
  return 7 + Math.floor(Math.random() * 4);
}

function shuffle_take<T>(arr: T[], n: number): T[] {
  if (arr.length === 0) {
    return [];
  }
  const take = Math.min(n, arr.length);
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  }
  return copy.slice(0, take);
}

function order_items_to_flat_strip(items: OrderMenuCartItem[]): FlatItem[] {
  return items.map(it => {
    const row = it as unknown as Record<string, unknown>;
    return {
      key: `order-strip-${it.id}`,
      name: getApiName(row) || it.name || 'Item',
      uri: resolveImageUri(it.image ?? it.attachment),
      quantity: 1,
    };
  });
}

export interface OnlineOrderImagesStripProps {
  colors: ThemeColors;
}

const s = menuImagesStripStyles;

const strip_styles = StyleSheet.create({
  title: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.7,
    textTransform: 'uppercase' as const,
    marginBottom: appTheme.spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: appTheme.spacing.sm,
    paddingTop: 0,
    paddingBottom: appTheme.spacing.xs,
  },
});

export function OnlineOrderImagesStrip({ colors }: OnlineOrderImagesStripProps) {
  const { t } = useTranslation();
  const rtl = useRtl();
  const text_align: 'left' | 'right' = rtl.isRTL ? 'right' : 'left';
  const card_pad = appTheme.card.padding.lg;
  const [strip_count] = useState(random_strip_count);
  const { data, isLoading } = useOrderMenuQuery();
  const { width: screen_width } = useWindowDimensions();
  const tile_metrics = useMemo(() => computeMenuImageTileMetrics(screen_width), [screen_width]);

  const locale = getLocale();
  const items = useMemo(() => {
    const all = data?.allItems ?? [];
    if (all.length === 0) {
      return [];
    }
    return order_items_to_flat_strip(shuffle_take(all, strip_count));
  }, [data?.allItems, strip_count, locale]);

  const catalog_empty = (data?.allItems?.length ?? 0) === 0;

  if (isLoading && catalog_empty) {
    return (
      <ContentCard
        variant="outlined"
        borderless
        padding="lg"
        style={[studioCardShell(colors), { backgroundColor: colors.surface, padding: 0, overflow: 'visible' }]}>
        <Text
          style={[
            strip_styles.title,
            { color: colors.textLight, paddingHorizontal: DASH_STUDIO_INNER, paddingTop: card_pad, marginBottom: appTheme.spacing.xs, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
          ]}
          numberOfLines={1}
          accessibilityRole="header">
          {t('dashboard.onlineOrderStripTitle')}
        </Text>
        <View style={[s.loadingWrap, { marginBottom: 0, paddingTop: 0, paddingBottom: card_pad }]}>
          <ButtonLoader size={18} color={colors.primary} accessibilityLabel={t('common.loading')} />
        </View>
      </ContentCard>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <ContentCard
      variant="outlined"
      borderless
      padding="lg"
      style={[studioCardShell(colors), { backgroundColor: colors.surface, padding: 0, overflow: 'visible' }]}>
      <Text
        style={[
          strip_styles.title,
          { color: colors.textLight, paddingHorizontal: DASH_STUDIO_INNER, paddingTop: card_pad, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
        ]}
        numberOfLines={1}
        accessibilityRole="header">
        {t('dashboard.onlineOrderStripTitle')}
      </Text>
      <HorizontalLtrScrollView
        style={[s.scrollView, { minHeight: tile_metrics.tile_height + 8 }]}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[
          strip_styles.row,
          { paddingHorizontal: card_pad, paddingBottom: card_pad + 4, paddingTop: appTheme.spacing.md, gap: appTheme.spacing.sm },
        ]}>
        {items.map(item => (
          <MenuImageTile key={item.key} item={item} colors={colors} tile_metrics={tile_metrics} />
        ))}
      </HorizontalLtrScrollView>
    </ContentCard>
  );
}
